import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Autocomplete, TextField, CircularProgress, Box, Typography, IconButton } from '@mui/material';
import { Person, Badge, Email, Phone, LocationOn, Search, SearchOff } from '@mui/icons-material';
import { receptorService } from '../../../../services/api';
import { DOCUMENT_TYPES, DOCUMENT_FORMATTERS, DOCUMENT_MAX_LENGTHS, getDocumentHelperText } from '../config/dteConfig';

/**
 * Componente de búsqueda de receptor con autocomplete y botón de búsqueda manual
 * El usuario escribe el número y hace clic en la lupa para buscar
 */
const ReceptorSearch = ({
  tipoDocumento,
  numDocumento,
  receptor,
  onSelect,
  onInputChange,
  label = 'Buscar Receptor',
  required = false,
  error = '',
  disabled = false,
  companyId = null,
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false); // Track if search was performed

  // Sincronizar inputValue con numDocumento de las props cuando cambia externamente
  useEffect(() => {
    if (numDocumento !== undefined && numDocumento !== inputValue) {
      setInputValue(numDocumento);
    }
  }, [numDocumento]);

  // Limpiar solo cuando cambia el tipo de documento
  useEffect(() => {
    setOptions([]);
    setHasSearched(false);
    // Reformatear el valor actual cuando cambia el tipo de documento
    if (inputValue && tipoDocumento) {
      const formattedValue = formatInputValue(inputValue, tipoDocumento);
      if (formattedValue !== inputValue) {
        setInputValue(formattedValue);
        if (onInputChange) {
          onInputChange(formattedValue);
        }
      }
    }
  }, [tipoDocumento]);

  const SEARCH_THRESHOLD = 2;

  // Función de búsqueda manual
  const handleSearch = useCallback(async () => {
    if (!tipoDocumento || !inputValue || inputValue.length < SEARCH_THRESHOLD) {
      setSearchError(`Ingrese al menos ${SEARCH_THRESHOLD} caracteres`);
      return;
    }

    setLoading(true);
    setSearchError(null);

    try {
      const results = await receptorService.search(tipoDocumento, inputValue, companyId);
      setOptions(results || []);
      setHasSearched(true); // Mark that search was performed
      setOpen(true);
      
      // Auto-seleccionar si hay exactamente 1 resultado
      if (results && results.length === 1) {
        onSelect(results[0]);
        setOpen(false);
      }
    } catch (err) {
      console.error('Error buscando receptores:', err);
      setSearchError('Error al buscar receptores');
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [inputValue, tipoDocumento, companyId, onSelect]);

  // Manejar cambio en input (solo actualiza el valor, no busca)
  const handleInputChange = (event, newInputValue, reason) => {
    // Aplicar formateo según el tipo de documento
    const formattedValue = formatInputValue(newInputValue, tipoDocumento);
    setInputValue(formattedValue);
    if (onInputChange) {
      onInputChange(formattedValue);
    }
  };

  // Manejar presión de tecla Enter en el campo de búsqueda
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      // Ejecutar la búsqueda
      if (tipoDocumento && inputValue && inputValue.length >= SEARCH_THRESHOLD) {
        handleSearch();
      }
    }
  };

  // Manejar cambio de selección
  const handleChange = (event, newValue) => {
    if (newValue && newValue.id && newValue.tipoDocumento) {
      onSelect(newValue);
    } else if (newValue && newValue.data) {
      onSelect(newValue.data);
    } else {
      onSelect(null);
    }
    setOpen(false);
  };

  const getDocumentTypeName = (code) => {
    const doc = DOCUMENT_TYPES.find(d => d.code === code);
    return doc ? doc.name : code || '';
  };

  const formatInputValue = (value, docType) => {
    if (!value || !docType) return value;
    const formatter = DOCUMENT_FORMATTERS[docType];
    if (formatter) {
      return formatter(value);
    }
    return value;
  };

  const getMaxLength = (docType) => {
    return DOCUMENT_MAX_LENGTHS[docType] || 20;
  };

  const formatOption = (option) => {
    if (!option || !option.id) return null;
    const docType = getDocumentTypeName(option.tipoDocumento);
    return {
      id: option.id || '',
      label: `${option.numDocumento || ''} - ${option.nombre || 'Sin nombre'}`,
      sublabel: docType,
      data: option,
    };
  };

  const currentValue = useMemo(() => {
    if (!receptor) return null;
    const formatted = formatOption(receptor);
    return formatted || null;
  }, [receptor]);

  const renderOption = (props, option) => {
    const { key, ...rest } = props;
    const data = option.data || option;
    const docType = getDocumentTypeName(data.tipoDocumento);
    
    return (
      <li key={key} {...rest}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={600}>
              {data.nombre || 'Sin nombre'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', ml: 4, gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              <Badge fontSize="small" sx={{ mr: 1 }} /> 
              {data.tipoDocumento || ''} - {data.numDocumento || ''}
              {data.nrc && ` | NRC: ${data.nrc}`}
            </Typography>
            {data.direccion?.complemento && (
              <Typography variant="caption" color="text.secondary">
                <LocationOn fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                {data.direccion.complemento}
              </Typography>
            )}
            {data.correo && (
              <Typography variant="caption" color="text.secondary">
                <Email fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                {data.correo}
              </Typography>
            )}
            {data.telefono && (
              <Typography variant="caption" color="text.secondary">
                <Phone fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                {data.telefono}
              </Typography>
            )}
          </Box>
        </Box>
      </li>
    );
  };

  const getNoOptionsText = () => {
    if (loading) return 'Buscando...';
    if (searchError) return searchError;
    if (!tipoDocumento) return 'Seleccione tipo de documento';
    // Si ya se buscó y no hay resultados, mostrar mensaje específico
    if (hasSearched && options.length === 0) {
      return 'No se encontraron resultados';
    }
    return 'Presione la lupa para buscar';
  };

  // Determinar si hay un cliente seleccionado
  const hasSelectedReceptor = receptor && receptor.id && receptor.nombre;
  
  const renderInput = (params) => {
    const maxLength = getMaxLength(tipoDocumento);
    const docTypeName = getDocumentTypeName(tipoDocumento);
    
    // Si hay un cliente seleccionado, mostrar botón de clear (X roja)
    // Si no hay cliente seleccionado, mostrar botón de búsqueda (lupa verde)
    // El botón siempre es visible
    const showClearButton = hasSelectedReceptor;
    const canSearch = !hasSelectedReceptor && tipoDocumento;
    const isDisabled = !canSearch || loading;
    
    const handleButtonClick = (e) => {
      e.stopPropagation();
      if (showClearButton) {
        // Limpiar selección
        setInputValue('');
        setOptions([]);
        onSelect(null);
        if (onInputChange) {
          onInputChange('');
        }
      } else if (canSearch) {
        // Ejecutar búsqueda
        handleSearch();
      }
    };
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
        <TextField
          {...params}
          label={label}
          required={required}
          error={!!error}
          helperText={error || (!tipoDocumento ? 'Seleccione tipo de documento primero' : `Formato: ${getDocumentHelperText(tipoDocumento)}`)}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <React.Fragment>
                <Search color="action" sx={{ ml: 1, mr: 0.5 }} />
              </React.Fragment>
            ),
            endAdornment: loading ? (
              <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} />
            ) : null,
          }}
          inputProps={{
            ...params.inputProps,
            maxLength: maxLength,
            onKeyDown: handleKeyDown,
          }}
          sx={{ 
            flex: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: '6px 0 0 6px',
            }
          }}
        />
        {/* Botón siempre visible */}
        <IconButton
          onClick={handleButtonClick}
          disabled={isDisabled}
          sx={{ 
            ml: '-1px',
            mt: '0px',
            height: '56px',
            minWidth: '40px',
            borderRadius: '0 6px 6px 0',
            border: '2px solid',
            borderColor: showClearButton ? 'error.main' : 'success.main',
            borderLeft: 'none',
            bgcolor: 'transparent',
            color: showClearButton ? 'error.main' : 'success.main',
            '&:hover': {
              bgcolor: showClearButton ? 'error.light' : 'success.light',
              color: 'white',
            },
            '&.Mui-disabled': {
              borderColor: 'action.disabled',
              color: 'action.disabled',
            }
          }}
        >
          {showClearButton ? (
            <SearchOff />
          ) : (
            <Search />
          )}
        </IconButton>
      </Box>
    );
  };

  return (
    <Box>
      <Autocomplete
        open={open}
        onOpen={() => options.length > 0 && setOpen(true)}
        onClose={() => setOpen(false)}
        loading={loading}
        value={currentValue}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        options={options}
        disabled={disabled || !tipoDocumento}
        getOptionLabel={(option) => option?.label || ''}
        isOptionEqualToValue={(option, val) => option?.id === (val?.id || val?.data?.id)}
        renderInput={renderInput}
        renderOption={renderOption}
        noOptionsText={getNoOptionsText()}
        loadingText="Buscando..."
        placeholder="Buscar receptor por número de documento"
        sx={{ minWidth: 300 }}
        filterOptions={(x) => x}
        freeSolo
        handleHomeEndKeys
        disableClearable
      />
    </Box>
  );
};

export default ReceptorSearch;
